/* eslint no-console: "off" */
import { noop } from 'lodash/fp';
import { HTTP_UNAUTHORIZED } from './constants';

const calcRetryMs = (retryCount) => (retryCount === 0 ? 0 : 1000 * Math.pow(2, retryCount - 1));

const HEARTBEAT_INTERVAL_MS = 10 * 1000;

const INIT = 0;
const CONNECTING = 1;
const CONNECTED = 2;
const AUTHD = 3;
const DISCONNECTED = 4;
const UNAVAILABLE = 5;

const STATUS_GOING_AWAY = 1001;

export const WebSocketClient = ({ onReceive = noop, onStatusChange = noop }) => {
    let state = INIT;

    const { host, protocol } = window.location;
    const messageQueue = [];

    let authMessage = null;
    let retryCount = 0;
    let nextRetry = 0;

    let websocket;
    let heartbeatId = null;

    function connect() {
        console.info('WebSocketClient.connect()');

        websocket = new WebSocket(`${protocol === 'http:' ? 'ws:' : 'wss:'}//${host}`);
        state = CONNECTING;

        websocket.onmessage = (message) => {
            if (message.data instanceof Blob) {
                const reader = new FileReader();

                reader.onloadend = (e) => {
                    receive(e.target.result);
                };

                reader.readAsText(message.data);
            } else {
                receive(message.data);
            }
        };

        websocket.onclose = (event) => {
            console.info('WebSocketClient.onClose()', event);

            websocket = null;
            state = DISCONNECTED;

            if (event.code === STATUS_GOING_AWAY) {
                // This happens when we navigate away from the page. For example, when
                // redirecting to the keycloak screen or doing a hard-refresh. In these
                // cases we don't want to attempt to reconnect or flash the "not
                // connected" overlay.
                return;
            }

            const ms = calcRetryMs(retryCount++);

            console.info('WebSocketClient will retry in', ms);

            nextRetry = Date.now() + ms;

            setTimeout(connect, ms);

            onStatusChange({ isConnected: false, nextRetry });
        };

        startHeartbeat();
    }

    function parseSystemStatus(status) {
        let systemStatus = null;

        try {
            systemStatus = JSON.parse(status);
        } catch {
            console.warn('Error parsing system status: ', status);
        }

        return systemStatus;
    }

    function receive(message) {
        const response = JSON.parse(message);

        if (response.status === 503 && !response.data?._type) {
            console.info('WebSocketClient got a 503', response);

            state = UNAVAILABLE;
            onStatusChange({
                isConnected: false,
                nextRetry: nextRetry || Date.now() + HEARTBEAT_INTERVAL_MS,
                systemStatus: parseSystemStatus(response.errors[0].message)
            });

            return;
        }

        if (response.status === HTTP_UNAUTHORIZED) {
            window.location.reload();
            return;
        }

        if (response._context?.heartbeat) {
            retryCount = 0;
            nextRetry = 0;

            if (state !== CONNECTED && state !== AUTHD) {
                state = CONNECTED;
                reAuthIfNecessary();
            }

            onStatusChange({ isConnected: true, nextRetry, systemStatus: response.data });

            return;
        }

        onReceive(response);
    }

    function reAuthIfNecessary() {
        if (state < AUTHD && authMessage) {
            send(authMessage);
        }
    }

    function send(message) {
        if (message.name === 'Authenticate') {
            state = AUTHD;
            authMessage = message;
            messageQueue.unshift(message);
        } else {
            reAuthIfNecessary();
            messageQueue.push(message);
        }

        processMessageQueue();
    }

    function socketIsReady() {
        return websocket && websocket.readyState;
    }

    function processMessageQueue() {
        if (socketIsReady() && state === AUTHD) {
            const message = messageQueue.shift();

            if (message) {
                websocket.send(JSON.stringify(message));

                if (messageQueue.length > 0) {
                    processMessageQueue();
                }
            }
        } else {
            setTimeout(processMessageQueue, 1000);
        }
    }

    function sendHeartbeat() {
        if (state === DISCONNECTED) {
            return;
        }

        if (socketIsReady()) {
            websocket.send(
                JSON.stringify({ _type: 'europa.web.Heartbeat', _context: { heartbeat: true } })
            );
        }

        let ms = 0;

        if (state === UNAVAILABLE) {
            ms = calcRetryMs(retryCount++);
            nextRetry = Date.now() + ms;
        }

        heartbeatId = setTimeout(
            sendHeartbeat,
            state === CONNECTED || state === AUTHD
                ? HEARTBEAT_INTERVAL_MS
                : state === CONNECTING
                  ? 500
                  : ms
        );
    }

    function startHeartbeat() {
        stopHeartbeat();
        sendHeartbeat();
    }

    function stopHeartbeat() {
        if (heartbeatId) {
            clearTimeout(heartbeatId);
        }
    }

    return {
        connect,
        send
    };
};
